import { messages } from "@/shared/i18n";
import { cn } from "@/shared/lib/cn";

interface CardSkeletonProps {
  className?: string;
}

/**
 * Pulsing skeleton that mirrors the Card layout — used as Suspense fallback
 * for lazy-loaded pages.
 *
 * @example
 * ```tsx
 * <Suspense fallback={<CardSkeleton />}>
 *   <LazyComponent />
 * </Suspense>
 * ```
 */
export function CardSkeleton({ className }: CardSkeletonProps) {
  return (
    <div
      aria-busy="true"
      aria-label={messages.components.accessibility.loading}
      className={cn("shadow-card animate-pulse rounded-2xl bg-white p-8", className)}
      role="status"
    >
      {/* Header */}
      <div className="mb-8 flex flex-col items-center gap-2">
        <div className="h-8 w-48 rounded-lg bg-gray-200" />
        <div className="h-4 w-64 rounded bg-gray-200" />
      </div>

      {/* Input rows */}
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-24 rounded bg-gray-200" />
            <div className="h-10 w-full rounded-lg bg-gray-200" />
          </div>
        ))}
      </div>

      {/* Button */}
      <div className="mt-6 h-12 w-full rounded-lg bg-gray-200" />
    </div>
  );
}
